import type {
    MapData,
    MapDataCategory,
    MapDataHeroAction,
    MapDataLocationFeature,
    MapDataSubcategory,
} from '@repo/types/maps/map-types';
import { buildCategoryMap, buildSubcategoryMap, isIncludedInChecklist } from '../checklist/checklist-utils';

export type ExploreAction =
    | {
          type: 'nearby';
          id: 'nearby';
      }
    | {
          type: 'checklist';
          id: string;
          category: MapDataCategory;
          subcategory: MapDataSubcategory;
      }
    | {
          type: 'hero_action';
          id: string;
          heroAction: MapDataHeroAction;
      }
    | {
          type: 'ask_anything';
          id: 'ask_anything';
      };

// TODO: Ask Anything stays hidden until it has a backend integration.
const isAskAnythingEnabled = false;

export function resolveExploreActions({
    mapData,
    mapExternalId,
    pinId,
}: {
    mapData: MapData | null;
    mapExternalId?: string;
    pinId?: string;
}): ExploreAction[] {
    const actions: ExploreAction[] = [{ type: 'nearby', id: 'nearby' }];
    const pin = resolvePin(mapData, pinId);

    if (pin && mapData) {
        const categoryMap = buildCategoryMap(mapData);
        const subcategoryMap = buildSubcategoryMap(mapData);
        const subcategory = subcategoryMap.get(pin.properties.subcategoryExternalId);
        const category = subcategory ? categoryMap.get(subcategory.categoryExternalId) : undefined;

        if (
            mapData.metadata.isChecklistPublished &&
            subcategory &&
            category &&
            isIncludedInChecklist(subcategory, category)
        ) {
            actions.push({
                type: 'checklist',
                id: `checklist:${subcategory.externalId}`,
                category,
                subcategory,
            });
        }

        actions.push(
            ...resolveHeroActionsForTriggerPin(mapData.heroActions ?? [], pin.properties.externalId, mapExternalId).map(
                heroAction => ({
                    type: 'hero_action' as const,
                    id: `hero_action:${heroAction.externalId}`,
                    heroAction,
                }),
            ),
        );
    }

    if (isAskAnythingEnabled) {
        actions.push({ type: 'ask_anything', id: 'ask_anything' });
    }

    return actions;
}

export function resolveHeroActionsForTriggerPin(
    heroActions: ReadonlyArray<MapDataHeroAction>,
    pinId: string | undefined,
    mapExternalId?: string,
): MapDataHeroAction[] {
    if (!pinId || !mapExternalId) return [];

    return heroActions
        .filter(action => (action.triggerPins[mapExternalId] ?? []).includes(pinId))
        .sort((a, b) => {
            if (b.priority !== a.priority) return b.priority - a.priority;
            return a.name.localeCompare(b.name) || a.externalId.localeCompare(b.externalId);
        });
}

function resolvePin(mapData: MapData | null, pinId: string | undefined): MapDataLocationFeature | null {
    if (!mapData || !pinId) return null;
    return (
        mapData.geojson.features.find(
            (feature): feature is MapDataLocationFeature =>
                feature.geometry.type === 'Point' && feature.properties.externalId === pinId,
        ) ?? null
    );
}
